import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import t from "@/lang/lang";
import { MonitorTypeUtil } from "@/services/backend/qpm/lib/monitor-type-util";
import { Button } from "@/shared/components/ui/button";
import { Plus, Trash2 } from "lucide-react";
import { Skeleton } from "@/shared/components/ui/skeleton";
import { MonitorType } from "@/services/backend/qpm/types/enums";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { CreateNewMonitorComponent } from "@/routes/base-data/processes/qpm/catalog/[catalogId]/_components/create-new-monitor-component";
import { useEffect, useMemo, useState } from "react";
import {
  useQpmCatalogCreateMonitorMutation,
  useQpmCatalogExcludeMonitorMutation,
  useQpmCatalogItemsListQuery,
} from "@/services/backend/qpm/catalog/api/catalog-api";
import { useQpmMonitorServiceDefaultsListQuery } from "@/services/backend/qpm/monitors/api/monitor-api";
import { CreateMonitorRequest } from "@/services/backend/qpm/catalog/types/catalog";
import { usePersistenceStateUpdateOnTrigger } from "@/shared/lib/persistence-state/persistence-state-info";
import { useNavigate } from "react-router";
import { MonitorSelectorComboBox } from "@/routes/base-data/processes/qpm/catalog/[catalogId]/_components/monitor-selector-combobox";
import { resolveMonitorDefaultTimes } from "@/services/backend/qpm/monitors/lib/default-service-times";
import { DialogTooltipButton } from "@/shared/components/dialog/dialog-tooltip-button";
import { useToast } from "@/shared/hooks/use-toast";
import { parseRTKQueryError } from "@/shared/components/errors/parse-r-t-k-query-error";
import { MonitorOverviewTable } from "@/routes/base-data/processes/qpm/_components/monitor-overview-table";

export function MonitorCatalog({
  catalogId,
  monitorTypeKey,
}: {
  catalogId: string;
  monitorTypeKey: MonitorType;
}) {
  const navigate = useNavigate();

  const {
    data: itemsRes,
    isLoading,
    error,
  } = useQpmCatalogItemsListQuery({
    catalogId,
    includedOnly: true,
    // keep backend defaults for paging for now
    limit: 0,
    offset: 0,
  });

  const items = useMemo(() => {
    const all = itemsRes?.items ?? [];
    return all.filter(
      (it) => it.included && it.snapshot?.type === monitorTypeKey,
    );
  }, [itemsRes?.items, monitorTypeKey]);

  const [request, setRequest] = useState<CreateMonitorRequest>({
    catalogId,
    monitorType: monitorTypeKey,
    name: "",
    install: resolveMonitorDefaultTimes(undefined, monitorTypeKey).install,
    maintain: resolveMonitorDefaultTimes(undefined, monitorTypeKey).maintain,
  });
  const { data: serviceDefaults } = useQpmMonitorServiceDefaultsListQuery({});

  const [
    createMonitor,
    {
      data: createdMonitor,
      isLoading: createIsLoading,
      isSuccess: createIsSuccess,
      error: createError,
      reset: createReset,
    },
  ] = useQpmCatalogCreateMonitorMutation();

  const { onTrigger: triggerSave } = usePersistenceStateUpdateOnTrigger(
    request,
    createMonitor,
    createIsLoading,
    createError,
    createIsSuccess,
    createReset,
    { toastError: true, toastSuccess: true },
  );

  useEffect(() => {
    if (createIsSuccess && createdMonitor && createdMonitor.monitorId !== "") {
      navigate(createdMonitor.monitorId);
    }
  }, [createIsSuccess, createdMonitor, navigate]);

  useEffect(() => {
    const defaults = resolveMonitorDefaultTimes(
      serviceDefaults,
      monitorTypeKey,
      request.iotClass,
    );
    setRequest((prev) => {
      if (
        prev.install === defaults.install &&
        prev.maintain === defaults.maintain
      ) {
        return prev;
      }
      return {
        ...prev,
        install: defaults.install,
        maintain: defaults.maintain,
      };
    });
  }, [serviceDefaults, monitorTypeKey, request.iotClass]);

  const rows = useMemo(
    () =>
      items.map((item) => ({
        id: item.catalogItemId,
        name: item.snapshot?.name ?? "",
        type: item.snapshot.type,
        includeInQpmService:
          !!item.snapshot.capabilities?.includeInCheckpointService,
        recommendedVisits: item.snapshot.recommendedVisits ?? 0,
        price: item.snapshot.basePrice?.price ?? 0,
        updatedAt: item.snapshot.takenAt,
        rentingFees: catalogMonitorRentingFeesToRows(item.snapshot),
        onEdit: () => navigate(item.baseMonitorId),
        trailingAction: (
          <RemoveCatalogMonitorButton
            catalogId={catalogId}
            monitorId={item.baseMonitorId}
            monitorName={item.snapshot?.name ?? ""}
          />
        ),
      })),
    [catalogId, items, navigate],
  );

  const [showCreateButton, setShowCreateButton] = useState(rows.length === 0);

  useEffect(() => {
    if (rows.length === 0) {
      setShowCreateButton(true);
    }
  }, [rows.length]);

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  if (isLoading) {
    return <MonitorCatalogLoadingState />;
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <MonitorSelectorComboBox
          catalogId={catalogId}
          monitorTypeKey={monitorTypeKey}
          onCreateButtonVisibilityChange={setShowCreateButton}
        />

        {showCreateButton && (
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="default">
                <Plus />
                {t("Neuen Monitor einfügen")}
              </Button>
            </DialogTrigger>
            <DialogContent className="">
              <DialogHeader>
                <DialogTitle>
                  {t("Neuen ")}
                  {MonitorTypeUtil.toLongString(
                    monitorTypeKey as MonitorType,
                  )}{" "}
                  {t("anlegen")}
                </DialogTitle>
                <DialogDescription>
                  {t("Erstellen Sie hier einen neuen ")}{" "}
                  {MonitorTypeUtil.toLongString(monitorTypeKey)}
                </DialogDescription>
              </DialogHeader>
              <CreateNewMonitorComponent
                updateRequest={setRequest}
                request={request}
              />
              <DialogFooter className="sm:justify-start">
                <DialogClose asChild>
                  <Button type="button" variant="secondary">
                    Close
                  </Button>
                </DialogClose>
                <Button
                  type="button"
                  variant="default"
                  onClick={triggerSave}
                  disabled={createIsLoading}
                >
                  {t("Erstellen")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <MonitorOverviewTable rows={rows} monitorType={monitorTypeKey} />
    </>
  );
}

function catalogMonitorRentingFeesToRows(snapshot: {
  type: MonitorType;
  rentingDefaults?: Record<number, number>;
}) {
  if (snapshot.type !== MonitorType.IoT) {
    return [];
  }
  return Object.entries(snapshot.rentingDefaults ?? {}).map(
    ([durationMonths, monthly]) => ({
      durationMonths: Number(durationMonths),
      monthly: Number(monthly),
    }),
  );
}

function MonitorCatalogLoadingState() {
  return (
    <div className="flex w-full flex-col gap-2">
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-10 w-full" />
    </div>
  );
}

function RemoveCatalogMonitorButton({
  catalogId,
  monitorId,
  monitorName,
}: {
  catalogId: string;
  monitorId: string;
  monitorName: string;
}) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [removeMonitor, { isLoading, isSuccess, error, reset }] =
    useQpmCatalogExcludeMonitorMutation();

  useEffect(() => {
    if (!isSuccess) {
      return;
    }

    setOpen(false);
    reset();
    toast({
      title: t("Monitor entfernt"),
      description: t("Der Monitor wurde aus dem Katalog entfernt."),
    });
  }, [isSuccess, reset, toast]);

  useEffect(() => {
    if (!error) {
      return;
    }

    const parsedError = parseRTKQueryError(error);
    toast({
      variant: "destructive",
      title: parsedError?.title ?? t("Fehler"),
      description: parsedError?.description ?? t("Unerwarteter Fehler"),
    });
  }, [error, toast]);

  return (
    <DialogTooltipButton
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) {
          reset();
        }
      }}
      tooltip={t("Aus Katalog entfernen")}
      dialogTitle={t("Monitor aus Katalog entfernen?")}
      icon={<Trash2 className="h-4 w-4" />}
      buttonProps={{ size: "sm", variant: "outline", disabled: isLoading }}
      content={
        <>
          <p className="text-muted-foreground text-sm">
            {monitorName
              ? `${monitorName} ${t("wird aus diesem Katalog entfernt.")}`
              : t("Dieser Monitor wird aus diesem Katalog entfernt.")}
          </p>
          <p className="text-muted-foreground text-sm">
            {t(
              'Der Basis Monitor bleibt erhalten und kann über "Monitor wählen oder anlegen" wieder hinzugefügt werden.',
            )}
          </p>
          <RTKQueryErrorAlert error={error} />
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">{t("Abbrechen")}</Button>
            </DialogClose>
            <Button
              variant="destructive"
              disabled={isLoading}
              onClick={() => removeMonitor({ catalogId, monitorId })}
            >
              {t("Löschen")}
            </Button>
          </DialogFooter>
        </>
      }
    />
  );
}
